import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Box,
  tableCellClasses,
  styled,
  TableSortLabel
} from '@mui/material';

interface ProductMovementData {
  _id: string;
  productName: string;
  variantAttributes: { [key: string]: string };
  barcode: string;
  totalQuantity: number;
  unitName: string;
  branchName?: string;
}

interface ProductMovementTableProps {
  data: ProductMovementData[];
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#f5f5f5',
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    fontSize: '0.95rem',
    padding: '16px 8px',
    textAlign: 'left'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.9rem',
    padding: '12px 8px',
    textAlign: 'left'
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: 'rgba(48, 195, 176, 0.08)',
    transition: 'background-color 0.2s ease'
  },
  '& > td': {
    borderBottom: '1px solid rgba(224, 224, 224, 0.5)'
  }
}));

const ProductMovementTable: React.FC<ProductMovementTableProps> = ({ data }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<'asc' | 'desc'>('desc');

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortRequest = () => {
    setOrderBy(orderBy === 'asc' ? 'desc' : 'asc');
  };

  // ترتيب البيانات حسب الكمية باستخدام useMemo لمنع إعادة الحساب غير الضرورية
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      // تحويل القيم إلى أرقام للتأكد من الترتيب الصحيح
      const quantityA = Number(a.totalQuantity) || 0;
      const quantityB = Number(b.totalQuantity) || 0;
      
      if (orderBy === 'asc') {
        return quantityA - quantityB;
      } else {
        return quantityB - quantityA;
      }
    });
  }, [data, orderBy]);

  const getVariantText = (attributes: { [key: string]: string }): string => {
    // التحقق من وجود الخصائص
    if (!attributes || typeof attributes !== 'object' || Object.keys(attributes).length === 0) {
      return '-';
    }
    
    // فلترة الخصائص واستبعاد الباركود
    const filteredAttributes = Object.entries(attributes)
      .filter(([key, value]) => {
        // استبعاد الباركود بجميع أشكاله
        const keyLower = key.toLowerCase();
        const isBarcode = keyLower === 'barcode' || keyLower === 'باركود' || key === 'barcode';
        
        // استبعاد القيم الفارغة
        const hasValue = value && value.toString().trim() !== '';
        
        return !isBarcode && hasValue;
      })
      .map(([key, value]) => `${key}: ${value}`) // عرض المفتاح والقيمة
      .filter(text => text);
    
    return filteredAttributes.length > 0 ? filteredAttributes.join(' | ') : '-';
  };

  const visibleRows = rowsPerPage === -1
    ? sortedData
    : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table stickyHeader aria-label="جدول حركة المنتجات">
          <TableHead>
            <TableRow>
              <StyledTableCell>الباركود</StyledTableCell>
              <StyledTableCell>اسم المنتج</StyledTableCell>
              <StyledTableCell>خصائص المتغير</StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={true}
                  direction={orderBy}
                  onClick={handleSortRequest}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      opacity: 1
                    }
                  }}
                >
                  إجمالي الكمية
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>الفرع</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row, index) => (
                <StyledTableRow key={`${row._id}-${index}`}>
                  <StyledTableCell>
                    <Typography variant="body2">
                      {row.barcode || '-'}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                      {row.productName}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2" color="text.secondary">
                      {getVariantText(row.variantAttributes)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ color: '#30c3b0' }}
                    >
                      {row.totalQuantity} {row.unitName}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2">
                      {row.branchName || '-'}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">لا توجد بيانات متاحة</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, { label: 'الكل', value: -1 }]}
        component="div"
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="عدد الصفوف:"
        labelDisplayedRows={
          ({ from, to, count }) => {
            if (rowsPerPage === -1) {
              return `عرض الكل (${count})`;
            }
            return `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`;
          }
        }
      />
    </Box>
  );
};

export default ProductMovementTable;